import { LeftOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { css } from 'styled-components';

import { Flex } from '@/components/base';
import Button from '@/components/Button/Button';
import IconButton from '@/components/Button/IconButton';
import FavoriteToggle from '@/components/Common/FavoriteToggle';
import Input from '@/components/Inputs/Input';
import Modal from '@/components/Modal/Modal';
import AMP_KEYS from '@/configs/amp-track-keys';
import {
  CHART_GRAPH,
} from '@/entities/chart/model/constants';
import AddChartToDashboardModal from '@/features/(charts)/chart-list/ui/add-chart-to-dashboard-modal/add-chart-to-dashboard-modal';
import { ChartDetailPreprocess } from '@/features/(charts)/chart-renderer/lib/ChartPreprocessFactory/ChartPreprocessFactory';
import {
  getNormalizedHistorical,
  getNormalizedSelectedField,
} from '@/features/(charts)/chart-renderer/lib/ChartPreprocessFactory/helper';
import { COMBO_CHART_ERROR_MESSAGES } from '@/features/(charts)/chart-renderer/model/error-messages';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import useModalState from '@/hooks/pure/useModalState';
import service from '@/services/index';
import useCommonStore from '@/stores/pure/CommonStore';
import { useToast } from '@/stores/pure/ToastStore';
import { track } from '@/utils/track/track';
import { clipboardCopy, isEmptyString } from '@/utils/util';

import { initialChartState } from '../hooks/use-chart-detail';

type Props = {
  chartInfo: ChartContextProps;
};

const ChartHeader = (props: Props) => {
  const queryClient = useQueryClient();
  const { prev: prevPath } = useCommonStore((state) => state.appPathHistory);
  const {
    state,
    setState,
    title,
    setTitle,
    originalChartInfo,
    setOriginalChartInfo,
    selectedField,
    setSelectedField,
  } = props.chartInfo;
  const wr = useWorkspaceRouter();
  const addToast = useToast();
  const addToDashboardModalState = useModalState<'add' | 'save'>('add');

  const hasChange = useMemo(() => {
    if (!originalChartInfo) return true;

    if (title !== originalChartInfo.name) return true;

    const keys = Object.keys(state) as (keyof typeof state)[];
    for (const key of keys) {
      const stateValue = state[key];
      const originValue = originalChartInfo[key as keyof ChartItem];

      const strStateValue = JSON.stringify(stateValue);
      const strOriginValue = JSON.stringify(originValue);

      const isNotMeaningfulFalsy = originValue !== false;
      const isEmptyOriginValue = !originValue;

      if (isNotMeaningfulFalsy && isEmptyOriginValue) continue;
      switch (key) {
        case 'selected_field': {
          const originSelectedValue = {
            ...(originValue as ChartSelectedFieldItemDef),
            items: (originValue as ChartSelectedFieldItemDef).items || [],
          };

          const currentSelectedValue = {
            ...selectedField,
            items: selectedField.items || [],
          };

          if (
            JSON.stringify(currentSelectedValue) !==
            JSON.stringify(originSelectedValue)
          ) {
            return true;
          }
          break;
        }
        case 'field': {
          const fieldValue = stateValue as ChartState['field'];
          const compareFieldValue: ChartFieldDef = {
            type: fieldValue.type,
            entire: fieldValue.entire,
            candidates: fieldValue.candidates,
          };
          if (JSON.stringify(compareFieldValue) !== strOriginValue) {
            return true;
          }
          break;
        }
        default: {
          if (strStateValue !== strOriginValue) {
            return true;
          }
          break;
        }
      }
    }
    return false;
  }, [originalChartInfo, selectedField, state, title]);

  const chartParams = {
    ...state,
    field: state.field,
    series: state.series,
    starred: originalChartInfo?.starred,
    name: title,
    selected_field: selectedField,
  };

  const createChart = async (
    callback?: (chart: ChartItem) => Promise<void>,
  ) => {
    const preprocessFactory = new ChartDetailPreprocess(chartParams);
    const result = preprocessFactory.runPreprocess();

    const res = await service.chart.createChart({
      ...result,
    });

    // Track chart create complete event
    track(AMP_KEYS.CHART_CREATE_COMPLETE, {
      chart_alias: res.alias,
      chart_type: res.type,
      graph_type: res.graph,
    });

    await callback?.(res);
    queryClient.invalidateQueries();
    wr.push(`/charts/${res.alias}`);
    addToast('Chart created', 'success');
  };

  const updateChart = async (
    callback?: (chart: ChartItem) => Promise<void>,
  ) => {
    if (!originalChartInfo) throw new Error('No chart alias');

    const preprocessFactory = new ChartDetailPreprocess(chartParams);
    const result = preprocessFactory.runPreprocess();
    const chartItem = await service.chart.updateChart(
      originalChartInfo.alias,
      result,
    );
    const normalizedSelectedField =
      getNormalizedSelectedField(chartItem.selected_field, chartItem.graph) ||
      initialChartState.selected_field;
    await callback?.(chartItem);
    queryClient.invalidateQueries();
    addToast('Chart updated', 'success');
    setOriginalChartInfo({
      ...chartItem,
      selected_field: normalizedSelectedField,
      graph_direction:
        chartItem.graph_direction ?? initialChartState.graph_direction,
    });
    setSelectedField(normalizedSelectedField);
    setTitle(chartItem.name);
    setState({
      type: chartItem.type,
      historical: getNormalizedHistorical(
        chartItem.historical || initialChartState.historical,
        (chartItem.target || initialChartState.target).term,
      ),
      graph: chartItem.graph ?? initialChartState.graph,
      selected_field: normalizedSelectedField,
      target: chartItem.target || initialChartState.target,
      graph_direction:
        chartItem.graph_direction ?? initialChartState.graph_direction,
      field: chartItem.field || initialChartState.field,
      series: chartItem.series || initialChartState.series,
    });
  };

  const onSaveClick = async () => {
    if (isEmptyString(title)) {
      addToast('Please enter a chart name', 'fail');
      return;
    }

    // COMBO_CHART 특별 검증
    if (state.graph === CHART_GRAPH.COMBO_CHART) {
      if (!state.series || state.series.length < 2) {
        addToast(COMBO_CHART_ERROR_MESSAGES.SECONDARY_MEASURE_MISSING, 'fail');
        return;
      }
    }

    if (props.chartInfo.mode === 'CREATE') {
      addToDashboardModalState.setModalState('save');
      addToDashboardModalState.open();
    } else {
      await updateChart();
    }
  };

  const onFavoriteChange = async (value: boolean) => {
    if (!originalChartInfo) throw new Error('No chart alias');
    await service.chart.toggleStarChart(originalChartInfo.alias, value);
    setOriginalChartInfo((prev) => (prev ? { ...prev, starred: value } : null));
    if (value) {
      addToast('Chart starred', 'success');
    } else {
      addToast('Chart unstarred', 'success');
    }
  };

  const onShareClick = () => {
    clipboardCopy(window.location.href);
    addToast('Link copied', 'success');
  };

  const onAddToClick = () => {
    addToDashboardModalState.setModalState('add');
    addToDashboardModalState.open();
  };

  const handleBackBtnClick = () => {
    if (prevPath) {
      // 현재 서비스 내 다른 페이지에서 유입된 경우
      wr.navigate(-1);
    } else {
      wr.push(`/settings/charts`);
    }
  };

  const onDuplicateClick = async () => {
    if (!originalChartInfo) return;
    const newChart = await service.chart.duplicateChart(
      originalChartInfo.alias,
    );
    addToast('Chart duplicated', 'success');
    wr.push(`/charts/${newChart.alias}`);
  };

  return (
    <Flex j="space-between" g={12} className="w-full py-2 items-center">
      <Flex g={8} f={1}>
        <IconButton
          icon={<LeftOutlined />}
          sizeType="l"
          onClick={handleBackBtnClick}
        />
        <Input
          css={css`
            background-color: ${(props) => props.theme.colors.gray200};
            border: 1px solid transparent;
            &:focus {
              border-color: ${(props) => props.theme.colors.primary100};
            }
          `}
          placeholder="type chart name"
          sizeType="l"
          wrapperStyle={{ flex: 1 }}
          defaultValue={title}
          onBlur={(e) => setTitle(e.target.value)}
        />
      </Flex>
      <Flex g={8} a="center">
        {originalChartInfo && (
          <>
            <FavoriteToggle
              value={originalChartInfo.starred}
              onChange={onFavoriteChange}
            />
            <Button variant="ghost" sizeType="m" onClick={onShareClick}>
              Share
            </Button>
          </>
        )}
        <Button variant="ghost" sizeType="m" onClick={onAddToClick}>
          Add to dashboard
        </Button>
        {originalChartInfo && (
          <Button
            variant="ghost"
            sizeType="m"
            onClick={onDuplicateClick}
            disabled={hasChange}
          >
            Duplicate
          </Button>
        )}
        <Button
          variant="primary"
          sizeType="m"
          onClick={onSaveClick}
          disabled={!hasChange}
        >
          Save
        </Button>
      </Flex>
      <Modal
        status={addToDashboardModalState.status}
        closeModal={addToDashboardModalState.close}
      >
        <AddChartToDashboardModal
          mode={addToDashboardModalState.modalState}
          createChart={createChart}
          updateChart={updateChart}
          chartId={originalChartInfo?.alias}
        />
      </Modal>
    </Flex>
  );
};



export default ChartHeader;
